import React, { Suspense, type JSX } from "react"
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical"

const MathComponent = React.lazy(() => import("./component"))

type SerializedMathNode = Spread<
  {
    latex: string
    displayMode: boolean
  },
  SerializedLexicalNode
>

export class MathNode extends DecoratorNode<JSX.Element> {
  __latex: string
  __displayMode: boolean

  static getType(): string {
    return "math"
  }

  static clone(node: MathNode): MathNode {
    return new MathNode(node.__latex, node.__displayMode, node.__key)
  }

  constructor(latex: string, displayMode: boolean, key?: NodeKey) {
    super(key)
    this.__latex = latex
    this.__displayMode = displayMode
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement(this.__displayMode ? "div" : "span")
    element.textContent = this.__displayMode ? `$$${this.__latex}$$` : `$${this.__latex}$`
    return { element }
  }

  createDOM(): HTMLElement {
    return document.createElement(this.__displayMode ? "div" : "span")
  }

  updateDOM(): false {
    return false
  }

  isInline(): boolean {
    return !this.__displayMode
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={<span>{this.__displayMode ? `$$${this.__latex}$$` : `$${this.__latex}$`}</span>}>
        <MathComponent latex={this.__latex} displayMode={this.__displayMode} />
      </Suspense>
    )
  }

  getTextContent(): string {
    return this.__displayMode ? `$$${this.__latex}$$` : `$${this.__latex}$`
  }

  exportJSON(): SerializedMathNode {
    return {
      ...super.exportJSON(),
      latex: this.__latex,
      displayMode: this.__displayMode,
      type: "math",
      version: 1,
    }
  }

  static importJSON(serializedNode: SerializedMathNode): MathNode {
    return $createMathNode(serializedNode.latex, serializedNode.displayMode)
  }
}

export function $createMathNode(latex: string, displayMode: boolean): MathNode {
  return $applyNodeReplacement(new MathNode(latex, displayMode))
}

export function $isMathNode(node: LexicalNode | null | undefined): node is MathNode {
  return node instanceof MathNode
}
